import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type { Group, GroupDetail, GroupListResponse } from '../../lib/types.js';

const groupsKey = (tenantId: string | undefined) => queryKeys.groups(tenantId ?? '');
const groupKey = (tenantId: string | undefined, groupId: string) =>
  queryKeys.group(tenantId ?? '', groupId);

/**
 * A group's scope/membership change alters every member's EFFECTIVE scopes,
 * which other features read: the Users table (`users` query) and the current
 * user's chat-access gate (`me`). Those queries live in other features and were
 * never refreshed by group writes — the source of the "added a scope, nothing
 * re-rendered" bug. Any group mutation that changes who-has-what must call this.
 */
const refreshEffectiveScopes = (queryClient: QueryClient, tenantId: string | undefined): void => {
  void queryClient.invalidateQueries({ queryKey: queryKeys.users(tenantId ?? '') });
  void queryClient.invalidateQueries({ queryKey: queryKeys.me() });
};

export const useGroupsListLogic = (tenantId: string | undefined) => {
  const query = useQuery<GroupListResponse>({
    queryKey: groupsKey(tenantId),
    queryFn: () => apiRequest<GroupListResponse>(`/api/groups`),
    enabled: Boolean(tenantId),
  });
  return { isLoading: query.isLoading, data: query.data?.groups, error: query.error };
};

export const useGroupDetailLogic = (tenantId: string | undefined, groupId: string | undefined) => {
  const query = useQuery<GroupDetail>({
    queryKey: groupKey(tenantId, groupId ?? ''),
    queryFn: () => apiRequest<GroupDetail>(`/api/groups/${groupId}`),
    enabled: Boolean(tenantId && groupId),
  });
  return { isLoading: query.isLoading, data: query.data, error: query.error };
};

export const useCreateGroupLogic = (tenantId: string | undefined, onSuccess: () => void) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);

  const mutation = useMutation<Group, Error, void>({
    mutationFn: () =>
      apiRequest<Group>(`/api/groups`, {
        method: 'POST',
        body: { name: name.trim(), scopes },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: groupsKey(tenantId) });
      onSuccess();
    },
  });

  return {
    name,
    setName,
    scopes,
    setScopes,
    isSubmitting: mutation.isPending,
    error: mutation.error,
    submit: async () => mutation.mutateAsync(),
    reset: () => {
      setName('');
      setScopes([]);
      mutation.reset();
    },
  };
};

export const useRenameGroupLogic = (tenantId: string | undefined) => {
  const queryClient = useQueryClient();
  const mutation = useMutation<Group, Error, { groupId: string; name: string }>({
    mutationFn: ({ groupId, name }) =>
      apiRequest<Group>(`/api/groups/${groupId}`, {
        method: 'PATCH',
        body: { name: name.trim() },
      }),
    onSuccess: (_g, { groupId }) => {
      void queryClient.invalidateQueries({ queryKey: groupsKey(tenantId) });
      void queryClient.invalidateQueries({ queryKey: groupKey(tenantId, groupId) });
    },
  });
  return {
    isSubmitting: mutation.isPending,
    error: mutation.error,
    rename: async (groupId: string, name: string) => mutation.mutateAsync({ groupId, name }),
  };
};

export const useDeleteGroupLogic = (tenantId: string | undefined) => {
  const queryClient = useQueryClient();
  const mutation = useMutation<void, Error, string>({
    mutationFn: (groupId) => apiRequest<void>(`/api/groups/${groupId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: groupsKey(tenantId) });
      refreshEffectiveScopes(queryClient, tenantId); // members lose the group's scopes
    },
  });
  return {
    isSubmitting: mutation.isPending,
    error: mutation.error,
    delete: async (groupId: string) => {
      await mutation.mutateAsync(groupId);
    },
  };
};

/**
 * Reconcile a group's scope set to `selected`. The API exposes add (POST) and
 * remove (DELETE :scope) — not a replace — so we diff against the current set
 * and issue only the deltas. Returns the updated detail via cache invalidation.
 */
export const useSetGroupScopesLogic = (tenantId: string | undefined) => {
  const queryClient = useQueryClient();
  const mutation = useMutation<
    void,
    Error,
    { groupId: string; current: string[]; selected: string[] }
  >({
    mutationFn: async ({ groupId, current, selected }) => {
      const added = selected.filter((s) => !current.includes(s));
      const removed = current.filter((s) => !selected.includes(s));
      const base = `/api/groups/${groupId}/scopes`;
      if (added.length > 0) {
        await apiRequest<Group>(base, { method: 'POST', body: { scopes: added } });
      }
      for (const scope of removed) {
        await apiRequest<Group>(`${base}/${encodeURIComponent(scope)}`, { method: 'DELETE' });
      }
    },
    onSuccess: (_v, { groupId }) => {
      void queryClient.invalidateQueries({ queryKey: groupsKey(tenantId) });
      void queryClient.invalidateQueries({ queryKey: groupKey(tenantId, groupId) });
      refreshEffectiveScopes(queryClient, tenantId); // members' effective scopes changed
    },
  });
  return {
    isSubmitting: mutation.isPending,
    error: mutation.error,
    setScopes: async (groupId: string, current: string[], selected: string[]) => {
      await mutation.mutateAsync({ groupId, current, selected });
    },
  };
};

// Single add/remove member hooks were superseded by useUsersBulkMembershipLogic
// (same endpoints; GroupManagePage routes its single-user Add through bulk).
